import type { IvrOption, OutboundCarrier } from '../../api/accounts';
import type {
  CosmosIvrConfig,
  CosmosOfficeHours,
  CosmosPhoneFeature,
  CosmosPhoneSettings,
} from '../../api/cosmos';
import type { RingStrategyName } from '../../api/ringStrategies';

export type WizardStepKey =
  | 'cosmos-search'
  | 'freeswitch-type'
  | 'infrastructure'
  | 'account'
  | 'users'
  | 'schedule'
  | 'audios'
  | 'resolution-strategies'
  | 'modules'
  | 'ivr'
  | 'queues'
  | 'phone-features';

export interface WizardStepMeta {
  key: WizardStepKey;
  label: string;
}

export const WIZARD_STEPS: WizardStepMeta[] = [
  { key: 'cosmos-search', label: 'Cosmos' },
  { key: 'freeswitch-type', label: 'Tipo FS' },
  { key: 'infrastructure', label: 'Infra' },
  { key: 'account', label: 'Cuenta' },
  { key: 'users', label: 'Usuarios' },
  { key: 'schedule', label: 'Horarios' },
  { key: 'audios', label: 'Audios' },
  { key: 'resolution-strategies', label: 'Resolución' },
  { key: 'modules', label: 'Modulos' },
  { key: 'ivr', label: 'IVR' },
  // The 'queues' step (QueuesStep) is hidden until provisioning uses its
  // agent-to-queue assignments: every module's call group is still built from
  // all in_call_center users, so the step would show choices that do nothing.
  { key: 'phone-features', label: 'Funciones' },
];

/**
 * Steps of the wizard for a FreeSWITCH type. Shared accounts get every step
 * except infrastructure; dedicated ones get the first four plus phone features
 * in production and all of them in the prototype.
 */
export const buildWizardSteps = (
  freeswitchType: FreeSwitchType,
  isPrototype: boolean
): WizardStepMeta[] => {
  if (freeswitchType === 'shared') {
    return WIZARD_STEPS.filter((s) => s.key !== 'infrastructure');
  }
  return isPrototype
    ? WIZARD_STEPS
    : WIZARD_STEPS.filter((s, idx) => idx < 4 || s.key === 'phone-features');
};

/** Day of week for schedule configuration */
export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

/** Time slot configuration for a single day */
export interface ScheduleTimeSlot {
  day_of_week: DayOfWeek;
  is_open: boolean;
  open_time: string; // HH:MM format
  close_time: string; // HH:MM format
}

/** Schedule configuration for the wizard */
export interface ScheduleConfig {
  time_slots: ScheduleTimeSlot[];
}

/** Day labels in Spanish */
export const DAY_LABELS: Record<DayOfWeek, string> = {
  mon: 'Lunes',
  tue: 'Martes',
  wed: 'Miércoles',
  thu: 'Jueves',
  fri: 'Viernes',
  sat: 'Sábado',
  sun: 'Domingo',
};

/** Creates default schedule (Mon-Fri 8am-6pm, Sat-Sun closed) */
export const createDefaultSchedule = (): ScheduleConfig => ({
  time_slots: [
    { day_of_week: 'mon', is_open: true, open_time: '08:00', close_time: '18:00' },
    { day_of_week: 'tue', is_open: true, open_time: '08:00', close_time: '18:00' },
    { day_of_week: 'wed', is_open: true, open_time: '08:00', close_time: '18:00' },
    { day_of_week: 'thu', is_open: true, open_time: '08:00', close_time: '18:00' },
    { day_of_week: 'fri', is_open: true, open_time: '08:00', close_time: '18:00' },
    { day_of_week: 'sat', is_open: false, open_time: '08:00', close_time: '18:00' },
    { day_of_week: 'sun', is_open: false, open_time: '08:00', close_time: '18:00' },
  ],
});

/** Mapping from Cosmos day names to wizard DayOfWeek */
const COSMOS_DAY_MAP: Record<string, DayOfWeek> = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
};

/** Order of days for consistent slot ordering */
const DAY_ORDER: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/**
 * Maps Cosmos office hours format to wizard ScheduleConfig.
 * Uses the first time range for each day (wizard only supports one range per day).
 */
export const mapCosmosOfficeHoursToSchedule = (cosmosHours: CosmosOfficeHours): ScheduleConfig => {
  const slotsByDay = new Map<DayOfWeek, ScheduleTimeSlot>();

  for (const [cosmosDay, dayConfig] of Object.entries(cosmosHours)) {
    const dayOfWeek = COSMOS_DAY_MAP[cosmosDay];
    if (!dayOfWeek) continue;

    // Use the first range, or defaults if no ranges
    const firstRange = dayConfig.ranges[0];
    slotsByDay.set(dayOfWeek, {
      day_of_week: dayOfWeek,
      is_open: dayConfig.enabled && dayConfig.ranges.length > 0,
      open_time: firstRange?.from || '08:00',
      close_time: firstRange?.to || '18:00',
    });
  }

  // Return slots in consistent day order
  const time_slots = DAY_ORDER.map(
    (day) =>
      slotsByDay.get(day) || {
        day_of_week: day,
        is_open: false,
        open_time: '08:00',
        close_time: '18:00',
      }
  );

  return { time_slots };
};

/** Account from Cosmos selected in the wizard */
export interface SelectedCosmosAccount {
  id: string;
  name: string;
  email: string;
  office_phone: string;
  timezone: string;
}

export type FreeSwitchType = 'dedicated' | 'shared';

export interface SelectedFreeSwitchGroup {
  id: number;
  name: string;
  freeswitch_uri: string;
  freeswitch_id: string;
}

export type PlaybackType = 'greeting' | 'hold_music' | 'announce' | 'message' | 'ivr_prompt';

export interface MockAudio {
  id: string;
  name: string;
  playback_type: PlaybackType;
  file_name: string;
  /** Selected local file (UI only until API wiring) */
  file?: File | null;
  /** Object URL for preview playback */
  preview_url?: string | null;
  /** Pre-loaded default audio URL (S3). Used when no file is uploaded. */
  default_url?: string | null;
  /** True if this is a system-provided default audio */
  is_default?: boolean;
}

/**
 * Default audio URLs hosted in S3.
 * These are used when creating a new account to provide example audios.
 */
export const DEFAULT_AUDIO_URLS = {
  greeting: 'https://phone-dev.s3.amazonaws.com/default_audio/greeting_es.wav',
  hold_music: 'https://phone-dev.s3.amazonaws.com/default_audio/hold_music_es.mp3',
  announce: 'https://phone-dev.s3.amazonaws.com/default_audio/announce_es.wav',
  message: 'https://phone-dev.s3.amazonaws.com/default_audio/message_es.wav',
  ivr_welcome: 'https://phone-dev.s3.amazonaws.com/default_audio/greeting_es.wav',
} as const;

export interface MockUser {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  cosmos_user_id: string;
  extension: string;
  is_admin: boolean;
  /** If true (default), user can receive inbound calls via call center queues */
  in_call_center: boolean;
  /** True if user was loaded from Cosmos (name/email read-only) */
  from_cosmos: boolean;
}

export interface MockModule {
  id: string;
  name: string;
  phone_number: string;
  in_office_greeting_id: string;
  in_office_hold_id: string;
  in_office_announce_id: string;
  out_of_office_message_id: string;
  ring_strategy: RingStrategyName;
  max_wait_time: number;
}

export type IvrActionType = 'module' | 'replay' | 'submenu' | 'back';

export const MAX_IVR_DEPTH = 3;

export interface MockIvrOption {
  id: string;
  digit: string;
  label: string;
  audio_id: string;
  queue_key: string;
  /**
   * 'module' routes to a call module; 'replay' replays the current menu;
   * 'submenu' opens `children`; 'back' returns to the parent menu (not allowed at root)
   */
  action_type?: IvrActionType;
  /** Options of the submenu opened by a 'submenu' option */
  children?: MockIvrOption[];
  /** Intro audio played when the submenu opens */
  menu_audio_id?: string;
}

export interface MockIvrConfig {
  enabled: boolean;
  language: 'es' | 'en';
  welcome_audio_id: string;
  options: MockIvrOption[];
}

/** Tag for a resolution strategy */
export interface MockResolutionTag {
  id: string;
  name: string;
}

/**
 * Resolution strategy configuration.
 * When an agent finishes a call, they classify it by selecting a tag.
 * This generates reports on call types and resolution metrics.
 */
export interface MockResolutionStrategy {
  id: string;
  name: string;
  /** Time in minutes the agent has to classify the call after hanging up */
  resolution_time: number;
  tags: MockResolutionTag[];
}

/**
 * Creates the default resolution strategy template.
 * This provides a starting point with common call classification tags.
 */
export const createDefaultResolutionStrategies = (): MockResolutionStrategy[] => [
  {
    id: 'default_strategy',
    name: 'Clasificación de llamadas',
    resolution_time: 2,
    tags: [
      { id: 't1', name: 'Cita agendada' },
      { id: 't2', name: 'Consulta general' },
    ],
  },
];

/** A reason agents can pick when going No disponible */
export interface MockUnavailableReason {
  id: string;
  name: string;
}

/** Interstellar's limit on a reason's name */
export const UNAVAILABLE_REASON_MAX_LENGTH = 64;

/**
 * Interstellar's default reasons, with an emoji in the name itself: reasons have
 * no icon field, and the name is what agents and supervisors see.
 */
export const createDefaultUnavailableReasons = (): MockUnavailableReason[] => [
  { id: 'ur_lunch', name: 'Almuerzo 🍽️' },
  { id: 'ur_meeting', name: 'Reunión 👥' },
  { id: 'ur_break', name: 'Break / baño 🚻' },
  { id: 'ur_patient', name: 'Atendiendo paciente 🩺' },
  { id: 'ur_other', name: 'Otro 📝' },
];

/** agentId → queue keys (module queue or IVR option queue) */
export type AgentQueueAssignments = Record<string, string[]>;

export interface WizardState {
  /** Selected Cosmos account from the first step */
  cosmos_account: SelectedCosmosAccount | null;
  /** True when users have been loaded from Cosmos for the selected account */
  cosmos_users_loaded: boolean;
  /** IVR configuration from Cosmos (if exists) */
  cosmos_ivr: CosmosIvrConfig | null;
  /** True when fetching IVR config from Cosmos */
  cosmos_ivr_loading: boolean;
  /**
   * True when reading the IVR from Cosmos failed, so the wizard can't tell
   * whether the account has one. Cleared once the user picks an IVR option.
   */
  cosmos_ivr_unavailable: boolean;
  /** True if user chose to import IVR from Cosmos instead of creating new */
  use_cosmos_ivr: boolean;
  /**
   * Phone features the Cosmos account already has, kept for the ones the wizard
   * doesn't ask about. Null while they aren't known (the fetch failed or the
   * UUID was typed by hand), so provisioning can't overwrite them blindly.
   */
  cosmos_phone_features: CosmosPhoneFeature[] | null;
  /** Phone features chosen in the wizard; IVR comes from the IVR step */
  phone_features: {
    conference: boolean;
    call_whisper: boolean;
    unavailable_reasons: boolean;
  };

  /** Type of FreeSWITCH: dedicated (new instance) or shared (existing group) */
  freeswitch_type: FreeSwitchType;
  /** Selected FreeSWITCH group when using shared mode */
  selected_group: SelectedFreeSwitchGroup | null;
  /** Maximum users allowed (UI validation only). Null = no limit. */
  max_users: number | null;

  cosmos_account_id: string;
  client_name: string;
  infra_ready: boolean;
  instance_id: string;
  efs_access_point_id: string;
  /** DSN, only for a FreeSWITCH that was provisioned by hand */
  pg_connection_string: string;
  /** `<database>@<host>` of a provisioned stack; its password stays in Secrets Manager */
  pg_database: string;
  /** Set once the stack is provisioned, so `POST /account/` resolves the DSN server-side */
  provisioned_client_name: string;
  /** CloudFormation stack driving the FreeSWITCH provisioning */
  stack_name: string;
  stack_status: string;
  stack_console_url: string;

  account_name: string;
  phone_number: string;
  /** Carrier that owns the main number and carries the outbound calls */
  outbound_carrier: OutboundCarrier;
  call_rescue_number: string;
  freeswitch_uri: string;
  admin_username: string;
  admin_password: string;
  search_index_name: string;

  users: MockUser[];
  audios: MockAudio[];
  modules: MockModule[];
  ivr: MockIvrConfig;
  resolution_strategies: MockResolutionStrategy[];
  /** Reasons created for the account when the unavailable reasons feature is on */
  unavailable_reasons: MockUnavailableReason[];
  schedule: ScheduleConfig;
  assignments: AgentQueueAssignments;
}

export const MOCK_DELAY_MS = 900;

/**
 * Creates default audios with pre-loaded S3 URLs.
 * Users can use these as-is or replace them with their own files.
 */
const createDefaultAudios = (): MockAudio[] => [
  {
    id: 'default_greeting',
    name: 'Saludo (ejemplo)',
    playback_type: 'greeting',
    file_name: 'greeting_es.wav',
    file: null,
    preview_url: null,
    default_url: DEFAULT_AUDIO_URLS.greeting,
    is_default: true,
  },
  {
    id: 'default_hold_music',
    name: 'Música en espera (ejemplo)',
    playback_type: 'hold_music',
    file_name: 'hold_music.wav',
    file: null,
    preview_url: null,
    default_url: DEFAULT_AUDIO_URLS.hold_music,
    is_default: true,
  },
  {
    id: 'default_announce',
    name: 'Anuncio (ejemplo)',
    playback_type: 'announce',
    file_name: 'announce_es.wav',
    file: null,
    preview_url: null,
    default_url: DEFAULT_AUDIO_URLS.announce,
    is_default: true,
  },
  {
    id: 'default_message',
    name: 'Fuera de horario (ejemplo)',
    playback_type: 'message',
    file_name: 'message_es.wav',
    file: null,
    preview_url: null,
    default_url: DEFAULT_AUDIO_URLS.message,
    is_default: true,
  },
  {
    id: 'default_ivr_welcome',
    name: 'Bienvenida IVR (ejemplo)',
    playback_type: 'ivr_prompt',
    file_name: 'ivr_welcome_es.wav',
    file: null,
    preview_url: null,
    default_url: DEFAULT_AUDIO_URLS.ivr_welcome,
    is_default: true,
  },
];

const createEmptyWizardState = (): WizardState => ({
  cosmos_account: null,
  cosmos_users_loaded: false,
  cosmos_ivr: null,
  cosmos_ivr_loading: false,
  cosmos_ivr_unavailable: false,
  use_cosmos_ivr: false,
  cosmos_phone_features: null,
  phone_features: { conference: false, call_whisper: false, unavailable_reasons: false },

  freeswitch_type: 'dedicated',
  selected_group: null,
  max_users: null,

  cosmos_account_id: '',
  client_name: '',
  infra_ready: false,
  instance_id: '',
  efs_access_point_id: '',
  pg_connection_string: '',
  pg_database: '',
  provisioned_client_name: '',
  stack_name: '',
  stack_status: '',
  stack_console_url: '',

  account_name: '',
  phone_number: '',
  outbound_carrier: 'twilio',
  call_rescue_number: '',
  freeswitch_uri: '',
  admin_username: '',
  admin_password: '',
  search_index_name: '',

  users: [],
  audios: createDefaultAudios(),
  modules: [],
  ivr: {
    enabled: false,
    language: 'es',
    welcome_audio_id: 'default_ivr_welcome',
    options: [],
  },
  resolution_strategies: createDefaultResolutionStrategies(),
  unavailable_reasons: createDefaultUnavailableReasons(),
  schedule: createDefaultSchedule(),
  assignments: {},
});

export const createInitialWizardState = (withDemoData = false): WizardState => {
  if (!withDemoData) {
    return createEmptyWizardState();
  }

  return {
    ...createEmptyWizardState(),
    cosmos_ivr: null,
    cosmos_ivr_loading: false,
    cosmos_ivr_unavailable: false,
    use_cosmos_ivr: false,
    cosmos_account_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    client_name: 'demo_client',
    account_name: 'Cuenta de demostracion',
    phone_number: '12025550100',
    call_rescue_number: '12025550101',
    freeswitch_uri: 'https://fs.example.invalid',
    admin_username: 'demo-admin',
    search_index_name: 'demo-client',

    users: [
      {
        id: 'u1',
        first_name: 'Ana',
        last_name: 'Garcia',
        email: 'ana@demo.com',
        cosmos_user_id: '10001',
        extension: '1000',
        is_admin: true,
        in_call_center: true,
        from_cosmos: true,
      },
      {
        id: 'u2',
        first_name: 'Luis',
        last_name: 'Martinez',
        email: 'luis@demo.com',
        cosmos_user_id: '10002',
        extension: '1001',
        is_admin: false,
        in_call_center: true,
        from_cosmos: true,
      },
      {
        id: 'u3',
        first_name: 'Maria',
        last_name: 'Lopez',
        email: 'maria@demo.com',
        cosmos_user_id: '10003',
        extension: '1002',
        is_admin: false,
        in_call_center: true,
        from_cosmos: true,
      },
    ],

    audios: [
      {
        id: 'a1',
        name: 'Saludo principal',
        playback_type: 'greeting',
        file_name: '',
        file: null,
        preview_url: null,
      },
      {
        id: 'a2',
        name: 'Musica en espera',
        playback_type: 'hold_music',
        file_name: '',
        file: null,
        preview_url: null,
      },
      {
        id: 'a3',
        name: 'Anuncio de posicion',
        playback_type: 'announce',
        file_name: '',
        file: null,
        preview_url: null,
      },
      {
        id: 'a4',
        name: 'Fuera de horario',
        playback_type: 'message',
        file_name: '',
        file: null,
        preview_url: null,
      },
      {
        id: 'a5',
        name: 'Bienvenida IVR',
        playback_type: 'ivr_prompt',
        file_name: '',
        file: null,
        preview_url: null,
      },
      {
        id: 'a6',
        name: 'Opcion citas',
        playback_type: 'ivr_prompt',
        file_name: '',
        file: null,
        preview_url: null,
      },
      {
        id: 'a7',
        name: 'Opcion facturacion',
        playback_type: 'ivr_prompt',
        file_name: '',
        file: null,
        preview_url: null,
      },
    ],

    modules: [
      {
        id: 'm1',
        name: 'Recepcion principal',
        phone_number: '12025550100',
        in_office_greeting_id: 'a1',
        in_office_hold_id: 'a2',
        in_office_announce_id: 'a3',
        out_of_office_message_id: 'a4',
        ring_strategy: 'round_robin',
        max_wait_time: 300,
      },
    ],

    ivr: {
      enabled: true,
      language: 'es',
      welcome_audio_id: 'a5',
      options: [
        {
          id: 'o1',
          digit: '1',
          label: 'Citas',
          audio_id: 'a6',
          queue_key: 'ivr_citas',
        },
        {
          id: 'o2',
          digit: '2',
          label: 'Facturacion',
          audio_id: 'a7',
          queue_key: 'ivr_facturacion',
        },
        {
          id: 'o3',
          digit: '0',
          label: 'Operador',
          audio_id: 'a1',
          queue_key: 'call_module_m1_in_office',
        },
      ],
    },

    assignments: {
      u1: ['call_module_m1_in_office', 'ivr_citas'],
      u2: ['call_module_m1_in_office', 'ivr_citas'],
      u3: ['ivr_facturacion'],
    },
  };
};

/** Port the account's FreeSWITCH serves its HTTP API on, the same on every instance. */
export const FREESWITCH_PORT = 8080;

/**
 * The URI Interstellar reaches a provisioned instance on.
 *
 * The address is the instance's private IP: the API talks to it from inside the
 * VPC, where no public address is assigned. Empty when the stack does not report
 * one, so the field is filled by hand instead of with a URI that resolves nowhere.
 */
export const freeswitchUri = (privateIp?: string | null): string =>
  privateIp ? `http://${privateIp}:${FREESWITCH_PORT}` : '';

export interface ProvisionedStack {
  client_name: string;
  instance_id?: string | null;
  instance_private_ip?: string | null;
  efs_access_point_id?: string | null;
  /** `<database>@<host>` of the generated database; its password stays in Secrets Manager */
  pg_database: string;
}

/**
 * Everything the Account step can read off the stack that provisioned the
 * FreeSWITCH, so the step is reached filled in rather than transcribed by hand.
 *
 * A value already in `state` wins: the fields are editable and a correction made
 * before following the stack must survive the next status read. Filled is read
 * the same way the step's own gate reads it, so a field left as whitespace is
 * written from the stack rather than counting as typed and blocking the step.
 */
export const accountFieldsFromStack = (
  state: WizardState,
  stack: ProvisionedStack
): Partial<WizardState> => {
  const keep = (typed: string, fromStack: string): string => (typed.trim() ? typed : fromStack);

  return {
    infra_ready: true,
    instance_id: stack.instance_id || '',
    efs_access_point_id: stack.efs_access_point_id || '',
    pg_database: stack.pg_database,
    provisioned_client_name: stack.client_name,
    account_name: keep(state.account_name, stack.client_name),
    admin_username: keep(state.admin_username, stack.client_name),
    search_index_name: keep(state.search_index_name, stack.client_name),
    freeswitch_uri: keep(state.freeswitch_uri, freeswitchUri(stack.instance_private_ip)),
  };
};

export const mockSleep = (ms = MOCK_DELAY_MS): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const moduleQueueKey = (moduleId: string): string => `call_module_${moduleId}_in_office`;

export const flattenIvrOptions = (options: MockIvrOption[]): MockIvrOption[] =>
  options.flatMap((o) => [o, ...flattenIvrOptions(o.children ?? [])]);

/** Maps each audio id to human-readable places (modules, IVR) that reference it. */
export const getAudioUsages = (
  state: Pick<WizardState, 'modules' | 'ivr'>
): Record<string, string[]> => {
  const usages: Record<string, string[]> = {};
  const add = (audioId: string | undefined, where: string): void => {
    if (!audioId) return;
    usages[audioId] = [...(usages[audioId] ?? []), where];
  };

  state.modules.forEach((m) => {
    const name = m.name || 'Módulo sin nombre';
    add(m.in_office_greeting_id, `Módulo "${name}" (saludo)`);
    add(m.in_office_hold_id, `Módulo "${name}" (música en espera)`);
    add(m.in_office_announce_id, `Módulo "${name}" (anuncio de cola)`);
    add(m.out_of_office_message_id, `Módulo "${name}" (fuera de horario)`);
  });

  if (state.ivr.enabled) {
    add(state.ivr.welcome_audio_id, 'IVR (bienvenida)');
    flattenIvrOptions(state.ivr.options).forEach((o) => {
      const label = o.label ? `${o.digit} - ${o.label}` : o.digit;
      add(o.audio_id, `IVR (opción ${label})`);
      add(o.menu_audio_id, `IVR (submenú ${label})`);
    });
  }

  return usages;
};

export const updateIvrOptionInTree = (
  options: MockIvrOption[],
  id: string,
  patch: Partial<MockIvrOption>
): MockIvrOption[] =>
  options.map((o) => {
    if (o.id === id) return { ...o, ...patch };
    return o.children ? { ...o, children: updateIvrOptionInTree(o.children, id, patch) } : o;
  });

export const removeIvrOptionFromTree = (options: MockIvrOption[], id: string): MockIvrOption[] =>
  options
    .filter((o) => o.id !== id)
    .map((o) => (o.children ? { ...o, children: removeIvrOptionFromTree(o.children, id) } : o));

export const addIvrChildToTree = (
  options: MockIvrOption[],
  parentId: string,
  child: MockIvrOption
): MockIvrOption[] =>
  options.map((o) => {
    if (o.id === parentId) return { ...o, children: [...(o.children ?? []), child] };
    return o.children ? { ...o, children: addIvrChildToTree(o.children, parentId, child) } : o;
  });

const IVR_ASSIGNABLE_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

/** First digit no option of the menu uses, or '' when every one is taken. */
export const nextFreeIvrDigit = (menu: MockIvrOption[]): string => {
  const usedDigits = new Set(menu.map((o) => o.digit.trim()));
  return IVR_ASSIGNABLE_DIGITS.find((d) => !usedDigits.has(d)) ?? '';
};

/**
 * Deep copy of an option and its submenus. Agents are assigned per queue_key,
 * so every copied option gets its own instead of sharing the original's.
 */
export const cloneIvrOption = (option: MockIvrOption, makeId: () => string): MockIvrOption => {
  const id = makeId();
  return {
    ...option,
    id,
    queue_key: `ivr_option_${id}`,
    children: option.children?.map((child) => cloneIvrOption(child, makeId)),
  };
};

/** Inserts a copy of the option right after it, on the first free digit of its menu. */
export const duplicateIvrOptionInTree = (
  options: MockIvrOption[],
  id: string,
  makeId: () => string
): MockIvrOption[] => {
  const index = options.findIndex((o) => o.id === id);
  if (index >= 0) {
    const original = options[index];
    const copy = {
      ...cloneIvrOption(original, makeId),
      digit: nextFreeIvrDigit(options),
      label: `${original.label} (copia)`,
    };
    return [...options.slice(0, index + 1), copy, ...options.slice(index + 1)];
  }
  return options.map((o) =>
    o.children ? { ...o, children: duplicateIvrOptionInTree(o.children, id, makeId) } : o
  );
};

export interface IvrValidationErrors {
  /** Errors of the root menu itself (e.g. no options) */
  root: string[];
  /** Errors per option id */
  byOption: Record<string, string[]>;
}

const validateIvrLevel = (options: MockIvrOption[], level: number): Record<string, string[]> => {
  const digitCounts = new Map<string, number>();
  options.forEach((o) => {
    const digit = o.digit.trim();
    if (digit) digitCounts.set(digit, (digitCounts.get(digit) ?? 0) + 1);
  });

  return options.reduce<Record<string, string[]>>((acc, o) => {
    const messages: string[] = [];
    const digit = o.digit.trim();
    if (!digit) messages.push('Falta el dígito');
    // '#' ends the caller's input in FreeSWITCH, so it can't be an option.
    else if (!/^[0-9*]$/.test(digit)) messages.push('El dígito debe ser 0-9 o *');
    else if ((digitCounts.get(digit) ?? 0) > 1) messages.push('Dígito repetido en este menú');
    if (!o.audio_id) messages.push('Falta el audio');
    if (level > MAX_IVR_DEPTH) messages.push(`Máximo ${MAX_IVR_DEPTH} niveles de submenú`);
    if (o.action_type === 'back' && level === 1) {
      messages.push('No se puede volver desde el menú principal');
    }
    const nested = o.action_type === 'submenu' ? validateIvrLevel(o.children ?? [], level + 1) : {};
    if (o.action_type === 'submenu' && (o.children ?? []).length === 0) {
      messages.push('El submenú necesita al menos una opción');
    }
    return { ...acc, ...nested, ...(messages.length > 0 ? { [o.id]: messages } : {}) };
  }, {});
};

export const validateIvrTree = (options: MockIvrOption[]): IvrValidationErrors => ({
  root: options.length === 0 ? ['Agrega al menos una opción'] : [],
  byOption: validateIvrLevel(options, 1),
});

export const isIvrTreeValid = (options: MockIvrOption[]): boolean => {
  const errors = validateIvrTree(options);
  return errors.root.length === 0 && Object.keys(errors.byOption).length === 0;
};

export const buildIvrOptionsPayload = (
  options: MockIvrOption[],
  context: {
    language: 'es' | 'en';
    primaryModuleId: number | undefined;
    audioUrl: (audioId: string) => string | undefined;
  }
): IvrOption[] =>
  options.map((opt) => {
    const optionAudioUrl = opt.audio_id ? context.audioUrl(opt.audio_id) : undefined;
    const actionType = opt.action_type || 'module';
    const isModule = actionType === 'module';
    const payload: IvrOption = {
      digit: opt.digit,
      label: opt.label,
      action_type: actionType,
      // Only module options route to a call module
      target_module_id: isModule ? context.primaryModuleId : undefined,
      audios: optionAudioUrl
        ? [{ language: context.language, selection_audio_url: optionAudioUrl }]
        : undefined,
    };
    if (actionType === 'submenu') {
      payload.options = buildIvrOptionsPayload(opt.children ?? [], context);
      payload.menu_audio_url = opt.menu_audio_id ? context.audioUrl(opt.menu_audio_id) : undefined;
    }
    return payload;
  });

export const getAvailableQueues = (state: WizardState): { key: string; label: string }[] => {
  const moduleQueues = state.modules.map((m) => ({
    key: moduleQueueKey(m.id),
    label: `${m.name} (in_office)`,
  }));

  if (!state.ivr.enabled) {
    return moduleQueues;
  }

  const ivrQueues = flattenIvrOptions(state.ivr.options)
    .filter((o) => o.action_type !== 'submenu' && o.action_type !== 'back')
    .map((o) => ({
      key: o.queue_key,
      label: `IVR ${o.digit} — ${o.label}`,
    }));

  const keys = new Set(moduleQueues.map((q) => q.key));
  return [...moduleQueues, ...ivrQueues.filter((q) => !keys.has(q.key))];
};

type IvrFeatureState = Pick<
  WizardState,
  'phone_features' | 'ivr' | 'cosmos_phone_features' | 'cosmos_ivr_unavailable'
>;

/**
 * Whether the account ends up with the IVR feature. It follows what was marked
 * in the IVR step; flows without that step, or where the Cosmos IVR couldn't
 * be read and the user hasn't chosen, keep whatever Cosmos already had.
 */
export const isIvrFeatureActive = (state: IvrFeatureState, hasIvrStep: boolean): boolean =>
  hasIvrStep && !state.cosmos_ivr_unavailable
    ? state.ivr.enabled
    : (state.cosmos_phone_features ?? []).includes('ivr');

/** The phone features to save in Cosmos when the account is provisioned. */
export const buildEnabledPhoneFeatures = (
  state: IvrFeatureState,
  hasIvrStep: boolean
): CosmosPhoneFeature[] => {
  const features: CosmosPhoneFeature[] = [];
  if (state.phone_features.conference) features.push('conference');
  if (isIvrFeatureActive(state, hasIvrStep)) features.push('ivr');
  if (state.phone_features.call_whisper) features.push('call_whisper');
  if (state.phone_features.unavailable_reasons) features.push('unavailable_reasons');
  return features;
};

/**
 * What to save in Cosmos when the account is provisioned. Cosmos writes only
 * the fields it receives, so when the account's current features couldn't be
 * read they are left out instead of being replaced by the wizard's defaults.
 */
export const buildCosmosPhoneSettings = (
  state: IvrFeatureState,
  hasIvrStep: boolean
): Partial<CosmosPhoneSettings> => ({
  has_soft_phone: true,
  ...(state.cosmos_phone_features !== null && {
    enabled_phone_features: buildEnabledPhoneFeatures(state, hasIvrStep),
  }),
});

type UnavailableReasonsState = Pick<
  WizardState,
  'phone_features' | 'cosmos_phone_features' | 'unavailable_reasons'
>;

/**
 * Whether provisioning creates the wizard's reasons. When the Cosmos features
 * couldn't be read the step hides the feature, so nothing is created.
 */
export const shouldCreateUnavailableReasons = (state: UnavailableReasonsState): boolean =>
  state.cosmos_phone_features !== null && state.phone_features.unavailable_reasons;

/** reason id → why Interstellar would refuse it */
export const getUnavailableReasonErrors = (
  reasons: MockUnavailableReason[]
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const seen = new Set<string>();
  reasons.forEach((reason) => {
    const name = reason.name.trim();
    const key = name.toLocaleLowerCase();
    if (!name) {
      errors[reason.id] = 'El motivo necesita un nombre';
    } else if (name.length > UNAVAILABLE_REASON_MAX_LENGTH) {
      errors[reason.id] = `Máximo ${UNAVAILABLE_REASON_MAX_LENGTH} caracteres`;
    } else if (seen.has(key)) {
      errors[reason.id] = 'Ya hay un motivo con este nombre';
    }
    seen.add(key);
  });
  return errors;
};

export const areUnavailableReasonsValid = (state: UnavailableReasonsState): boolean =>
  !shouldCreateUnavailableReasons(state) ||
  (state.unavailable_reasons.length > 0 &&
    Object.keys(getUnavailableReasonErrors(state.unavailable_reasons)).length === 0);

/** The reasons to create in Interstellar, in the order the agents will see them. */
export const buildUnavailableReasonsPayload = (
  reasons: MockUnavailableReason[]
): { name: string; position: number }[] =>
  reasons.map((reason, position) => ({ name: reason.name.trim(), position }));
